"use client";

/**
 * HeroEcosystem — v5 (Ecosystem Orbit)
 *
 * "Écosystème" is now visualized directly: Velyq logo at center + 8 tool icons
 * orbiting on 2 dashed rings. Makes the ecosystem concept immediate and
 * distinctive — no more generic dashboard screenshot.
 *
 * Pattern adapted from Magic UI's OrbitingCircles (magicui.design), remapped
 * to Velyq tokens (Radix OKLCH purple @280°, Cabinet Grotesk display font).
 *
 * Layout: centered text on top, orbit visual below, single CTA.
 * Typography: "écosystème" is a standalone display-font tagline under the
 * main H1 so it visually registers even before the visual.
 */
import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { motion } from "motion/react";
import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MagneticButton } from "@/components/landing/MagneticButton";
import { ArrowRight, Briefcase, Check, FileText, Linkedin, Mail, MessageSquare, Mic, Puzzle, Search, Sparkles, type LucideIcon } from "lucide-react";

/* ──────────────────────────────────────────────
   Orbit component (pure CSS rotation)
   ────────────────────────────────────────────── */

type OrbitTool = {
  Icon: LucideIcon;
  label: string;
  color: string;
};
function OrbitRing(t0) {
  const $ = _c(25);
  const {
    tools,
    radius,
    duration,
    reverse: t1,
    iconSize: t2
  } = t0;
  const reverse = t1 === undefined ? false : t1;
  const iconSize = t2 === undefined ? 44 : t2;
  const t3 = radius * 2;
  const t4 = radius * 2;
  const t5 = -radius;
  const t6 = -radius;
  let t7;
  if ($[0] !== t3 || $[1] !== t4 || $[2] !== t5 || $[3] !== t6) {
    t7 = {
      width: t3,
      height: t4,
      marginLeft: t5,
      marginTop: t6
    };
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = `${reverse ? "orbit-ccw" : "orbit-cw"} ${duration}s linear infinite`;
  let t9;
  if ($[5] !== t8) {
    t9 = {
      animation: t8
    };
    $[5] = t8;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== duration || $[8] !== iconSize || $[9] !== radius || $[10] !== reverse || $[11] !== tools) {
    let t11;
    if ($[13] !== duration || $[14] !== iconSize || $[15] !== radius || $[16] !== reverse || $[17] !== tools.length) {
      t11 = (tool, i) => {
        const angle = i / tools.length * 360;
        const rad = angle * Math.PI / 180;
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;
        return <div key={tool.label} className="pointer-events-auto absolute left-1/2 top-1/2" style={{
          marginLeft: -iconSize / 2,
          marginTop: -iconSize / 2,
          width: iconSize,
          height: iconSize,
          transform: `translate(${x}px, ${y}px)`
        }}><div className="orbit-ring group relative flex h-full w-full items-center justify-center rounded-2xl border backdrop-blur-xl transition-all duration-300 hover:scale-[1.2]" style={{
            borderColor: `${tool.color}40`,
            background: `linear-gradient(135deg, ${tool.color}1A 0%, oklch(0.15 0.03 280 / 0.7) 100%)`,
            boxShadow: `0 12px 32px -8px ${tool.color}55, 0 0 0 1px ${tool.color}12 inset, 0 2px 0 0 ${tool.color}18 inset`,
            animation: `${reverse ? "orbit-cw" : "orbit-ccw"} ${duration}s linear infinite`
          }}><tool.Icon className="h-[55%] w-[55%]" style={{
              color: tool.color,
              filter: `drop-shadow(0 2px 8px ${tool.color}66)`
            }} strokeWidth={2.25} /><span className="pointer-events-none absolute left-1/2 top-full z-20 mt-3 -translate-x-1/2 whitespace-nowrap rounded-lg border px-3 py-1.5 text-[11px] font-semibold opacity-0 shadow-xl backdrop-blur-md transition-all duration-200 group-hover:opacity-100" style={{
              borderColor: `${tool.color}50`,
              background: "oklch(0.12 0.02 280 / 0.95)",
              color: tool.color,
              boxShadow: `0 8px 24px -4px ${tool.color}44`
            }}><span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t" style={{
                borderColor: `${tool.color}50`,
                background: "oklch(0.12 0.02 280 / 0.95)"
              }} />{tool.label}</span><span className="pointer-events-none absolute -inset-2 rounded-[18px] opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
              background: `radial-gradient(circle, ${tool.color}35, transparent 65%)`
            }} /></div></div>;
      };
      $[13] = duration;
      $[14] = iconSize;
      $[15] = radius;
      $[16] = reverse;
      $[17] = tools.length;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    t10 = tools.map(t11);
    $[7] = duration;
    $[8] = iconSize;
    $[9] = radius;
    $[10] = reverse;
    $[11] = tools;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div className="orbit-ring relative h-full w-full" style={t9}>{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t7) {
    t12 = <div className="pointer-events-none absolute left-1/2 top-1/2" style={t7}>{t11}</div>;
    $[22] = t11;
    $[23] = t7;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function OrbitPath(t0) {
  const $ = _c(5);
  const {
    radius
  } = t0;
  const t1 = radius * 2;
  const t2 = radius * 2;
  const t3 = -radius;
  const t4 = -radius;
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = <div className="pointer-events-none absolute left-1/2 top-1/2 rounded-full border border-dashed border-border/30" style={{
      width: t1,
      height: t2,
      marginLeft: t3,
      marginTop: t4
    }} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  return t5;
}

/* ──────────────────────────────────────────────
   Central hub — Velyq brand mark
   ────────────────────────────────────────────── */

function CentralHub() {
  const $ = _c(9);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0,
      scale: 0.8
    };
    t1 = {
      opacity: 1,
      scale: 1
    };
    t2 = {
      duration: 0.6,
      delay: 0.2
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="pointer-events-none absolute -inset-20 -z-30 rounded-full bg-[#d946ef]/15 blur-[60px]" />;
    t4 = <span className="pointer-events-none absolute -inset-14 -z-20 rounded-full bg-primary/25 blur-[48px]" />;
    t5 = <span className="pointer-events-none absolute -inset-8 -z-10 rounded-full bg-primary/30 blur-2xl" />;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <motion.span className="pointer-events-none absolute h-36 w-36 rounded-full border border-primary/30" animate={{
      scale: [1, 1.35, 1],
      opacity: [0.6, 0, 0.6]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: "easeOut"
    }} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <motion.span className="pointer-events-none absolute h-36 w-36 rounded-full border border-[#d946ef]/20" animate={{
      scale: [1, 1.55, 1],
      opacity: [0.4, 0, 0.4]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: "easeOut",
      delay: 0.8
    }} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <motion.div initial={t0} animate={t1} transition={t2} className="pointer-events-none relative flex items-center justify-center">{t3}{t4}{t5}{t6}{t7}<div className="pointer-events-none relative flex h-28 w-28 items-center justify-center"><Image src="/velyq_icon.png" alt="Velyq" width={104} height={104} className="drop-shadow-[0_0_32px_rgba(59,130,246,0.55)]" priority={true} /></div></motion.div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
}

/* ──────────────────────────────────────────────
   Connection line rays (SVG, static pulse)
   ────────────────────────────────────────────── */

function ConnectionRays() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" width="500" height="500" viewBox="0 0 500 500" fill="none"><defs><radialGradient id="rayGradient" cx="50%" cy="50%"><stop offset="0%" stopColor="oklch(0.65 0.25 280)" stopOpacity="0.25" /><stop offset="100%" stopColor="oklch(0.65 0.25 280)" stopOpacity="0" /></radialGradient></defs>{Array.from({
        length: 8
      }).map(_temp)}<circle cx="250" cy="250" r="240" fill="url(#rayGradient)" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/* ──────────────────────────────────────────────
   Main orbit viz
   ────────────────────────────────────────────── */
function _temp(_, i) {
  const angle = i / 8 * 360 + 22.5;
  const rad = angle * Math.PI / 180;
  const x2 = 250 + Math.cos(rad) * 228;
  const y2 = 250 + Math.sin(rad) * 228;
  return <motion.line key={i} x1="250" y1="250" x2={x2} y2={y2} stroke="oklch(0.65 0.25 280)" strokeWidth="0.5" strokeOpacity="0.14" initial={{
    pathLength: 0
  }} animate={{
    pathLength: 1
  }} transition={{
    duration: 1.2,
    delay: 0.5 + i * 0.08
  }} />;
}
const ALL_TOOLS: OrbitTool[] = [{
  Icon: MessageSquare,
  label: "Préparation d'entretien",
  color: "#a855f7"
}, {
  Icon: Search,
  label: "Analyse ATS",
  color: "#818cf8"
}, {
  Icon: FileText,
  label: "Création de CV",
  color: "#c084fc"
}, {
  Icon: Mail,
  label: "Lettres de motivation",
  color: "#a78bfa"
}, {
  Icon: Mic,
  label: "Simulation vocale",
  color: "#d946ef"
}, {
  Icon: Linkedin,
  label: "Optimisation LinkedIn",
  color: "#e879f9"
}, {
  Icon: Briefcase,
  label: "Suivi des candidatures",
  color: "#7c3aed"
}, {
  Icon: Puzzle,
  label: "Extension Chrome",
  color: "#6366f1"
}];
function EcosystemOrbit() {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ALL_TOOLS.slice(0, 4);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const innerTools = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ALL_TOOLS.slice(4, 8);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const outerTools = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ConnectionRays />;
    t3 = <OrbitPath radius={130} />;
    t4 = <OrbitPath radius={220} />;
    t5 = <OrbitRing tools={innerTools} radius={130} duration={24} iconSize={46} />;
    t6 = <OrbitRing tools={outerTools} radius={220} duration={38} reverse={true} iconSize={50} />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="orbit-group relative mx-auto h-[500px] w-full max-w-[520px] sm:h-[540px]">{t2}{t3}{t4}{t5}{t6}<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><CentralHub /></div></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}

// Mobile/tablet fallback — orbit with radius 220 overflows below lg breakpoint
// (needs ~500px container). A static 2×4 grid communicates the same 8-tool
// ecosystem without motion overhead on low-end phones.
function StaticToolsGrid() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-6 flex justify-center"><div className="relative flex h-20 w-20 items-center justify-center"><span className="absolute -inset-6 rounded-full bg-primary/25 blur-2xl" /><span className="absolute -inset-3 rounded-full bg-[#d946ef]/20 blur-xl" /><Image src="/velyq_icon.png" alt="Velyq" width={72} height={72} className="relative drop-shadow-[0_0_24px_rgba(168,85,247,0.5)]" priority={true} /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-auto w-full max-w-md">{t0}<div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">{ALL_TOOLS.map(_temp2)}</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/* ──────────────────────────────────────────────
   Hero title
   ────────────────────────────────────────────── */
function _temp2(tool) {
  return <div key={tool.label} className="flex flex-col items-center gap-2 rounded-xl border p-3 text-center backdrop-blur-sm" style={{
    borderColor: `${tool.color}35`,
    background: `linear-gradient(135deg, ${tool.color}14 0%, oklch(0.15 0.03 280 / 0.55) 100%)`,
    boxShadow: `0 8px 20px -10px ${tool.color}33`
  }}><tool.Icon className="h-5 w-5" style={{
      color: tool.color,
      filter: `drop-shadow(0 1px 4px ${tool.color}66)`
    }} strokeWidth={2.25} /><span className="text-[11px] font-medium leading-tight text-foreground/85">{tool.label}</span></div>;
}
function HeroTitle(t0) {
  const $ = _c(6);
  const {
    locale
  } = t0;
  if (locale === "fr") {
    let t1;
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="block">Toute ta recherche</span>;
      t2 = <span className="block">d'emploi, dans un</span>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t1 = $[0];
      t2 = $[1];
    }
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <h1 className="font-display text-4xl font-bold leading-[1.02] tracking-tight text-balance sm:text-5xl md:text-6xl lg:text-7xl lg:text-[4.75rem]">{t1}{t2}<span className="block">seul{" "}<span className="bg-gradient-to-br from-[#c084fc] via-primary to-[#d946ef] bg-clip-text text-transparent">écosystème.</span></span></h1>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="block">Your entire job</span>;
    t2 = <span className="block">search in one</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h1 className="font-display text-5xl font-bold leading-[1.02] tracking-tight text-balance sm:text-6xl md:text-7xl lg:text-[4.75rem]">{t1}{t2}<span className="block">single{" "}<span className="bg-gradient-to-br from-[#c084fc] via-primary to-[#d946ef] bg-clip-text text-transparent">ecosystem.</span></span></h1>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/* ──────────────────────────────────────────────
   Main Hero
   ────────────────────────────────────────────── */

type HeroEcosystemProps = {
  locale: string;
  badge: string;
  subtitle: string;
  ctaLabel: string;
  ctaHref: string;
  reassurance: string;
};
export function HeroEcosystem(t0) {
  const $ = _c(51);
  const {
    locale,
    badge,
    subtitle,
    ctaLabel,
    ctaHref,
    reassurance
  } = t0;
  const fr = locale === "fr";
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 -z-10 overflow-hidden"><div className="absolute left-1/2 top-0 h-[700px] w-[1100px] -translate-x-1/2 rounded-full bg-primary/[0.10] blur-[140px]" /><div className="absolute -left-20 top-40 h-[400px] w-[400px] rounded-full bg-[#818cf8]/[0.08] blur-[100px]" /><div className="absolute -right-20 top-60 h-[400px] w-[400px] rounded-full bg-[#d946ef]/[0.08] blur-[100px]" /></div>;
    t2 = <div className="absolute inset-0 hero-grid" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      y: 10
    };
    t4 = {
      opacity: 1,
      y: 0
    };
    t5 = {
      duration: 0.4,
      delay: 0.05
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Sparkles className="h-3 w-3 text-primary" />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== badge) {
    t7 = <Badge variant="secondary" className="gap-1.5 px-3 py-1">{t6}<span className="text-xs">{badge}</span></Badge>;
    $[6] = badge;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/70" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" /></span>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = fr ? "8 outils \xB7 1 \xE9cosyst\xE8me" : "8 tools \xB7 1 ecosystem";
  let t10;
  if ($[9] !== t9) {
    t10 = <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground backdrop-blur">{t8}{t9}</span>;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10 || $[12] !== t7) {
    t11 = <motion.div initial={t3} animate={t4} transition={t5} className="flex flex-wrap items-center gap-2">{t7}{t10}</motion.div>;
    $[11] = t10;
    $[12] = t7;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  let t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: 0,
      y: 16
    };
    t13 = {
      opacity: 1,
      y: 0
    };
    t14 = {
      duration: 0.6,
      delay: 0.15
    };
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
  } else {
    t12 = $[14];
    t13 = $[15];
    t14 = $[16];
  }
  let t15;
  if ($[17] !== locale) {
    t15 = <motion.div initial={t12} animate={t13} transition={t14}><HeroTitle locale={locale} /></motion.div>;
    $[17] = locale;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  let t17;
  let t18;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      opacity: 0,
      y: 12
    };
    t17 = {
      opacity: 1,
      y: 0
    };
    t18 = {
      duration: 0.5,
      delay: 0.3
    };
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t16 = $[19];
    t17 = $[20];
    t18 = $[21];
  }
  let t19;
  if ($[22] !== subtitle) {
    t19 = <motion.p initial={t16} animate={t17} transition={t18} className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{subtitle}</motion.p>;
    $[22] = subtitle;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  let t21;
  let t22;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      opacity: 0,
      y: 12
    };
    t21 = {
      opacity: 1,
      y: 0
    };
    t22 = {
      duration: 0.5,
      delay: 0.45
    };
    $[24] = t20;
    $[25] = t21;
    $[26] = t22;
  } else {
    t20 = $[24];
    t21 = $[25];
    t22 = $[26];
  }
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ArrowRight className="ml-2 h-4 w-4" />;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  if ($[28] !== ctaLabel) {
    t24 = <Button size="lg" className="h-12 px-8 text-base shadow-[0_16px_40px_-8px_oklch(0.65_0.25_280/0.5)]">{ctaLabel}{t23}</Button>;
    $[28] = ctaLabel;
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  let t25;
  if ($[30] !== ctaHref || $[31] !== t24) {
    t25 = <MagneticButton strength={0.35}><Link href={ctaHref}>{t24}</Link></MagneticButton>;
    $[30] = ctaHref;
    $[31] = t24;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  let t26;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Check className="h-3 w-3 text-primary" />;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== reassurance) {
    t27 = <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">{t26}<span>{reassurance}</span></div>;
    $[34] = reassurance;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== t25 || $[37] !== t27) {
    t28 = <motion.div initial={t20} animate={t21} transition={t22} className="mt-2 flex flex-col items-start gap-3">{t25}{t27}</motion.div>;
    $[36] = t25;
    $[37] = t27;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== t11 || $[40] !== t15 || $[41] !== t19 || $[42] !== t28) {
    t29 = <div className="flex flex-col items-start gap-6 lg:col-span-7">{t11}{t15}{t19}{t28}</div>;
    $[39] = t11;
    $[40] = t15;
    $[41] = t19;
    $[42] = t28;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  let t31;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      opacity: 0,
      scale: 0.92
    };
    t31 = {
      opacity: 1,
      scale: 1
    };
    $[44] = t30;
    $[45] = t31;
  } else {
    t30 = $[44];
    t31 = $[45];
  }
  let t32;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      duration: 0.8,
      delay: 0.3,
      ease: [0.16, 1, 0.3, 1]
    };
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  let t33;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="w-full lg:hidden"><StaticToolsGrid /></div>;
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  let t34;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <motion.div initial={t30} animate={t31} transition={t32} className="relative flex w-full items-center justify-center lg:col-span-5">{t33}<div className="hidden lg:block"><EcosystemOrbit /></div></motion.div>;
    $[48] = t34;
  } else {
    t34 = $[48];
  }
  let t35;
  if ($[49] !== t29) {
    t35 = <section className="noise relative overflow-hidden pb-0">{t1}{t2}<div className="container relative mx-auto px-4 py-12 sm:py-16 lg:py-20"><div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">{t29}{t34}</div></div></section>;
    $[49] = t29;
    $[50] = t35;
  } else {
    t35 = $[50];
  }
  return t35;
}